'use client';
import Carousel from 'react-bootstrap/Carousel';
import Image from 'next/image';
import { BiSolidChevronRight, BiSolidChevronLeft } from 'react-icons/bi';
import Button from '@/components/button/button';
import './HeroCarousel.scss';
import { GetCaseStudySettingsResult } from '@/sanity.types';
import { urlForImage } from '@/sanity/lib/utils';
import Link from 'next/link';
import { imageData } from '@/config/defaultBlur';

interface HeroCarouselProps {
  carouselContent: NonNullable<GetCaseStudySettingsResult>['featureCaseStudy'];
  carousalLogoContent: NonNullable<GetCaseStudySettingsResult>['featuredCaseStudyLogos'];
  carousalBackgroundContent: NonNullable<GetCaseStudySettingsResult>['featuredCaseStudyBackground'];
}

const HeroCarousel: React.FC<HeroCarouselProps> = ({
  carouselContent,
  carousalLogoContent,
  carousalBackgroundContent,
}) => {
  return (
    <Carousel
      slide={false}
      fade={true}
      pause="hover"
      interval={5000}
      aria-label="Feature case studies carousel"
      aria-live="polite"
      className="hero-casestudies--carousel"
      nextIcon={<BiSolidChevronRight />}
      prevIcon={<BiSolidChevronLeft />}
    >
      {carouselContent.map((slide, index) => {
        const logoItem = carousalLogoContent[index];
        const backgroundItem = carousalBackgroundContent[index];
        const logoUrl = logoItem?.logo
          ? urlForImage(logoItem.logo)?.url()
          : null;
        return (
          <Carousel.Item
            key={slide?.id}
            aria-label={`Slide ${index + 1} of ${carouselContent?.length}`}
            role="region"
          >
            <div className="carousel-background">
              <div className="carousel-background-wrap">
                <Image
                  src={
                    (backgroundItem?.background &&
                      urlForImage(backgroundItem?.background)?.url()) ||
                    '/n_images/default-blog.jpg'
                  }
                  alt="Carousel Background"
                  layout="fill"
                  objectFit="cover"
                  priority
                  quality={100}
                  placeholder="blur"
                  blurDataURL={
                    backgroundItem?.background?.imgAsset?.metadata?.lqip ||
                    imageData.blurImage
                  }
                />
              </div>
              <div className="carousel-overlay">
                <Carousel.Caption>
                  <div className="container h-100">
                    <div className="row justify-content-end align-items-center h-100">
                      <div className="col-md-4 col-lg-6 slider-small--image">
                        <Link
                          href={`/case-studies/${slide?.buttonLink || ''}`}
                          className="image-preview"
                        >
                          {logoUrl && (
                            <Image
                              src={
                                (logoItem?.logo &&
                                  (urlForImage(
                                    logoItem.logo
                                  )?.url() as string)) ||
                                '/n_images/default-blog.jpg'
                              }
                              alt={logoItem?.altText || ''}
                              sizes="25vw"
                              width={500}
                              height={500}
                              className="carousel-small-image fade-in-out"
                              placeholder="blur"
                              quality={100}
                              priority={true}
                              blurDataURL={
                                logoItem?.logo?.imgAsset?.metadata?.lqip ||
                                imageData.blurImage
                              }
                            />
                          )}
                        </Link>
                      </div>
                      <div className="col-md-8 col-lg-6 slider-content-container">
                        <h3 className="carousel-title">{slide?.title}</h3>
                        <p className="carousel-paragraph">
                          {slide?.description}
                        </p>
                        <div className="buttons">
                          <Button
                            size="lg"
                            variant="secondary"
                            href={`/case-studies/${slide?.buttonLink || ''}`}
                          >
                            {slide?.buttonText}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Carousel.Caption>
              </div>
            </div>
          </Carousel.Item>
        );
      })}
    </Carousel>
  );
};

export default HeroCarousel;
